import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  User,
  Lock,
  Bell,
  Palette,
  Globe,
  Shield,
  History,
  Camera,
  Check,
  Loader2,
  Plus,
  Trash2,
  Copy,
  KeyRound,
  type LucideIcon,
} from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { useUIStore } from '@/stores/uiStore'
import { apiClient, apiKeysApi, filesApi, getApiErrorMessage } from '@/services/api'
import { Badge } from '@/components/common/Badge'
import { Modal } from '@/components/common/Modal'
import { confirm } from '@/lib/confirm'
import { cn } from '@/lib/utils'
import type { ApiKey } from '@/types'

// ============================================================
// Notification preferences storage key
// ============================================================
const NOTIF_PREFS_KEY = 'equipe-notification-prefs'

interface NotificationPrefs {
  taskAssigned: boolean
  taskDue: boolean
  commentMention: boolean
  projectUpdate: boolean
  clientUpdate: boolean
  emailDigest: boolean
}

const DEFAULT_NOTIF_PREFS: NotificationPrefs = {
  taskAssigned: true,
  taskDue: true,
  commentMention: true,
  projectUpdate: false,
  clientUpdate: false,
  emailDigest: true,
}

function loadNotifPrefs(): NotificationPrefs {
  try {
    const raw = localStorage.getItem(NOTIF_PREFS_KEY)
    if (!raw) return DEFAULT_NOTIF_PREFS
    return { ...DEFAULT_NOTIF_PREFS, ...JSON.parse(raw) }
  } catch {
    return DEFAULT_NOTIF_PREFS
  }
}

// saveNotifPrefs is used in components below; components call apiClient directly.
function saveNotifPrefsLocal(prefs: NotificationPrefs): void {
  localStorage.setItem(NOTIF_PREFS_KEY, JSON.stringify(prefs))
}

// ============================================================
// Tabs
// ============================================================

type SettingsTab = 'profile' | 'security' | 'preferences' | 'notifications'

const TABS: { id: SettingsTab; label: string; icon: LucideIcon }[] = [
  { id: 'profile', label: 'Profilo', icon: User },
  { id: 'security', label: 'Sicurezza', icon: Lock },
  { id: 'preferences', label: 'Preferenze', icon: Palette },
  { id: 'notifications', label: 'Notifiche', icon: Bell },
]

const inputClass = cn(
  'w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg',
  'focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20',
  'placeholder:text-slate-400 transition-colors'
)

const labelClass = 'block text-xs font-medium text-slate-700 mb-1.5'

function SectionHeader({ title, description }: { title: string; description?: string }) {
  return (
    <div className="mb-4">
      <h3 className="text-sm font-semibold text-slate-900 tracking-tight">{title}</h3>
      {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
    </div>
  )
}

// ============================================================
// Profile Tab (with avatar upload)
// ============================================================

function ProfileTab() {
  const user = useAuthStore((state) => state.user)
  const setUser = useAuthStore((state) => state.setUser)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [formData, setFormData] = useState({
    firstName: user?.firstName ?? '',
    lastName: user?.lastName ?? '',
    email: user?.email ?? '',
    phone: user?.phone ?? '',
    timezone: user?.timezone ?? 'Europe/Rome',
  })

  const [avatarUrl, setAvatarUrl] = useState<string>(user?.avatar ?? '')
  const [avatarUploading, setAvatarUploading] = useState(false)
  const [avatarError, setAvatarError] = useState('')
  const [saved, setSaved] = useState(false)
  const [saveError, setSaveError] = useState('')

  // Profile save mutation
  const mutation = useMutation({
    mutationFn: (data: typeof formData & { avatar?: string }) =>
      apiClient.patch('/auth/profile', data),
    onSuccess: (result) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (user) setUser({ ...user, ...(result as any) })
      setSaveError('')
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    },
    onError: (err: unknown) => {
      setSaveError(getApiErrorMessage(err))
    },
    meta: { silent: true },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaveError('')
    mutation.mutate({
      ...formData,
      ...(avatarUrl ? { avatar: avatarUrl } : {}),
    })
  }

  async function handleAvatarChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return

    setAvatarError('')

    // Validate file type and size
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']
    if (!allowedTypes.includes(file.type)) {
      setAvatarError('Formato non supportato. Usa JPG, PNG, WebP o GIF.')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setAvatarError('File troppo grande. Massimo 5 MB.')
      return
    }

    setAvatarUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const response = await filesApi.upload(formData)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const url: string = (response as any)?.data?.url ?? (response as any)?.url ?? ''
      if (!url) throw new Error('URL non ricevuto dal server')

      setAvatarUrl(url)
      // Immediately update profile with new avatar
      const updateResult = await apiClient.patch('/auth/profile', { avatar: url })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (user) setUser({ ...user, ...(updateResult as any), avatar: url })
    } catch (err) {
      setAvatarError(err instanceof Error ? err.message : 'Errore durante il caricamento')
    } finally {
      setAvatarUploading(false)
      // Reset file input so the same file can be selected again
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const userInitials = `${formData.firstName.charAt(0)}${formData.lastName.charAt(0)}`.toUpperCase()
  const effectiveAvatar = avatarUrl || user?.avatar

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <SectionHeader title="Informazioni Personali" description="Aggiorna le tue informazioni di profilo" />

      {/* Avatar */}
      <div className="flex items-center gap-4">
        <div className="relative group">
          {effectiveAvatar ? (
            <img
              src={effectiveAvatar}
              alt={`${formData.firstName} ${formData.lastName}`}
              className="w-16 h-16 rounded-full object-cover border-2 border-slate-200"
            />
          ) : (
            <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center">
              <span className="text-lg font-semibold text-white">{userInitials || 'U'}</span>
            </div>
          )}

          {/* Upload overlay button */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={avatarUploading}
            className={cn(
              'absolute inset-0 rounded-full flex items-center justify-center transition-all',
              'bg-black/0 group-hover:bg-black/40',
              'opacity-0 group-hover:opacity-100'
            )}
            title="Cambia foto profilo"
          >
            {avatarUploading ? (
              <Loader2 className="w-5 h-5 text-white animate-spin" />
            ) : (
              <Camera className="w-5 h-5 text-white" />
            )}
          </button>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={handleAvatarChange}
          />
        </div>

        <div>
          <p className="text-sm font-medium text-slate-900">
            {formData.firstName} {formData.lastName}
          </p>
          <p className="text-xs text-slate-400 capitalize">
            {user?.role?.name?.toLowerCase().replace(/_/g, ' ')}
          </p>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={avatarUploading}
            className="mt-1 text-xs text-primary-600 hover:text-primary-700 font-medium disabled:opacity-50"
          >
            {avatarUploading ? 'Caricamento...' : 'Cambia foto'}
          </button>
          {avatarError && (
            <p className="text-xs text-danger-600 mt-0.5">{avatarError}</p>
          )}
        </div>
      </div>

      {/* Name */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="profile-firstName" className={labelClass}>Nome</label>
          <input
            id="profile-firstName"
            type="text"
            className={inputClass}
            value={formData.firstName}
            onChange={(e) => setFormData((p) => ({ ...p, firstName: e.target.value }))}
          />
        </div>
        <div>
          <label htmlFor="profile-lastName" className={labelClass}>Cognome</label>
          <input
            id="profile-lastName"
            type="text"
            className={inputClass}
            value={formData.lastName}
            onChange={(e) => setFormData((p) => ({ ...p, lastName: e.target.value }))}
          />
        </div>
      </div>

      {/* Email */}
      <div>
        <label htmlFor="profile-email" className={labelClass}>Email</label>
        <input
          id="profile-email"
          type="email"
          className={inputClass}
          value={formData.email}
          onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
        />
      </div>

      {/* Phone */}
      <div>
        <label htmlFor="profile-phone" className={labelClass}>Telefono</label>
        <input
          id="profile-phone"
          type="tel"
          className={inputClass}
          value={formData.phone}
          onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))}
          placeholder="+39 02 1234567"
        />
      </div>

      {/* Timezone */}
      <div>
        <label htmlFor="profile-timezone" className={cn(labelClass, 'flex items-center gap-1.5')}>
          <Globe className="w-3.5 h-3.5 text-slate-400" />
          Fuso Orario
        </label>
        <select
          id="profile-timezone"
          className={inputClass}
          value={formData.timezone}
          onChange={(e) => setFormData((p) => ({ ...p, timezone: e.target.value }))}
        >
          <option value="Europe/Rome">Europe/Rome (UTC+1)</option>
          <option value="Europe/London">Europe/London (UTC+0)</option>
          <option value="America/New_York">America/New_York (UTC-5)</option>
          <option value="America/Los_Angeles">America/Los_Angeles (UTC-8)</option>
        </select>
      </div>

      <div className="flex items-center gap-3 pt-2">
        <button
          type="submit"
          disabled={mutation.isPending}
          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
        >
          {mutation.isPending ? 'Salvataggio...' : 'Salva Modifiche'}
        </button>
        {saved && (
          <span className="flex items-center gap-1.5 text-xs text-success-600 font-medium">
            <Check className="w-3.5 h-3.5" />
            Salvato!
          </span>
        )}
      </div>
      {saveError && <p className="text-xs text-danger-600">{saveError}</p>}
    </form>
  )
}

// ============================================================
// Security Tab
// ============================================================

function SecurityTab() {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  })
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)

  const mutation = useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) =>
      apiClient.post('/auth/change-password', data),
    onSuccess: () => {
      setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' })
      setError('')
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    },
    onError: (err: unknown) => {
      setError(getApiErrorMessage(err))
    },
    meta: { silent: true },
  })

  const queryClient = useQueryClient()
  const [newKeyLabel, setNewKeyLabel] = useState('')
  const [revealedKey, setRevealedKey] = useState<{ key: string; label: string } | null>(null)
  const [keyCopied, setKeyCopied] = useState(false)

  const { data: apiKeys } = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => apiKeysApi.list().then((r) => r.data as ApiKey[]),
  })

  const createKeyMutation = useMutation({
    mutationFn: (label: string) => apiKeysApi.create(label).then((r) => r.data),
    onSuccess: (data) => {
      setRevealedKey({ key: data.key, label: data.label })
      setNewKeyLabel('')
      queryClient.invalidateQueries({ queryKey: ['api-keys'] })
    },
  })

  const revokeKeyMutation = useMutation({
    mutationFn: (id: string) => apiKeysApi.revoke(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] })
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (formData.newPassword !== formData.confirmPassword) {
      setError('Le password non corrispondono')
      return
    }
    if (formData.newPassword.length < 8) {
      setError('La password deve avere almeno 8 caratteri')
      return
    }
    setError('')
    mutation.mutate({ currentPassword: formData.currentPassword, newPassword: formData.newPassword })
  }

  return (
    <>
    <form onSubmit={handleSubmit} className="space-y-6">
      <SectionHeader title="Modifica Password" description="Mantieni il tuo account al sicuro" />

      <div>
        <label htmlFor="security-currentPassword" className={labelClass}>Password Attuale</label>
        <input
          id="security-currentPassword"
          type="password"
          autoComplete="current-password"
          className={inputClass}
          value={formData.currentPassword}
          onChange={(e) => {
            setFormData((p) => ({ ...p, currentPassword: e.target.value }))
            setError('')
          }}
          placeholder="••••••••"
        />
      </div>

      <div>
        <label htmlFor="security-newPassword" className={labelClass}>Nuova Password</label>
        <input
          id="security-newPassword"
          type="password"
          autoComplete="new-password"
          className={inputClass}
          value={formData.newPassword}
          onChange={(e) => {
            setFormData((p) => ({ ...p, newPassword: e.target.value }))
            setError('')
          }}
          placeholder="Minimo 8 caratteri"
        />
      </div>

      <div>
        <label htmlFor="security-confirmPassword" className={labelClass}>Conferma Password</label>
        <input
          id="security-confirmPassword"
          type="password"
          autoComplete="new-password"
          className={cn(inputClass, error && 'border-danger-500 focus:border-danger-500 focus:ring-danger-500/30')}
          value={formData.confirmPassword}
          onChange={(e) => {
            setFormData((p) => ({ ...p, confirmPassword: e.target.value }))
            setError('')
          }}
          placeholder="Ripeti la nuova password"
        />
      </div>

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={mutation.isPending || !formData.currentPassword || !formData.newPassword}
          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
        >
          {mutation.isPending ? 'Aggiornamento...' : 'Aggiorna Password'}
        </button>
        {saved && (
          <span className="flex items-center gap-1.5 text-xs text-success-600 font-medium">
            <Check className="w-3.5 h-3.5" />
            Password aggiornata!
          </span>
        )}
      </div>
      {error && <p className="text-xs text-danger-600">{error}</p>}

      <div className="border-t border-slate-200 pt-6">
        <SectionHeader title="Sessioni Attive" description="Gestisci i dispositivi connessi" />
        <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-2xl border border-slate-200">
          <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex-shrink-0">
            <Shield className="w-4 h-4" />
          </span>
          <div>
            <p className="text-sm font-medium text-slate-800">Browser corrente</p>
            <p className="text-xs text-slate-500">Connesso ora</p>
          </div>
          <Badge variant="success" dot className="ml-auto">Attivo</Badge>
        </div>
      </div>

      <div className="border-t border-slate-200 pt-6">
        <SectionHeader title="Le mie chiavi API" description="Genera chiavi personali per autenticarti verso le API di JARVIS al posto di email e password" />

        <div className="flex items-center gap-2 mb-4">
          <input
            type="text"
            value={newKeyLabel}
            onChange={(e) => setNewKeyLabel(e.target.value)}
            className={inputClass}
            placeholder="Etichetta (es. Script automazione)"
          />
          <button
            type="button"
            onClick={() => createKeyMutation.mutate(newKeyLabel)}
            disabled={!newKeyLabel.trim() || createKeyMutation.isPending}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors whitespace-nowrap"
          >
            <Plus className="w-4 h-4" />
            Genera nuova chiave
          </button>
        </div>

        {apiKeys && apiKeys.length > 0 ? (
          <div className="space-y-2">
            {apiKeys.map((key) => (
              <div key={key.id} className="flex items-center gap-3 p-4 bg-slate-50 rounded-2xl border border-slate-200">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex-shrink-0">
                  <KeyRound className="w-4 h-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-800 truncate">{key.label}</p>
                  <p className="text-xs text-slate-500">
                    <code className="font-mono">jrv_{key.prefix}…</code>
                    {' · '}Creata il {new Date(key.createdAt).toLocaleDateString('it-IT')}
                    {' · '}{key.lastUsedAt ? `Ultimo utilizzo il ${new Date(key.lastUsedAt).toLocaleDateString('it-IT')}` : 'Mai usata'}
                  </p>
                </div>
                <Badge variant={key.revokedAt ? 'neutral' : 'success'} dot className="flex-shrink-0">
                  {key.revokedAt ? 'Revocata' : 'Attiva'}
                </Badge>
                {!key.revokedAt && (
                  <button
                    type="button"
                    onClick={async () => {
                      if (await confirm({ title: 'Revocare questa chiave?', message: `"${key.label}" smetterà di funzionare immediatamente. L'azione non è reversibile.`, danger: true, confirmLabel: 'Revoca' })) {
                        revokeKeyMutation.mutate(key.id)
                      }
                    }}
                    className="p-1.5 text-slate-400 hover:text-danger-600 transition-colors flex-shrink-0"
                    title="Revoca"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-400">Nessuna chiave API generata</p>
        )}
      </div>
    </form>

    <Modal
      open={!!revealedKey}
      onOpenChange={(o) => { if (!o) setRevealedKey(null) }}
      title="Chiave API generata"
      description="Copiala ora: non potrà più essere visualizzata"
    >
      {revealedKey && (
        <div className="space-y-4">
          <code className="block w-full text-xs font-mono bg-slate-50 border border-slate-200 rounded-lg p-3 break-all select-all">
            {revealedKey.key}
          </code>
          <p className="text-xs text-danger-600">
            Copiala ora — non sarà più visibile in nessun modo, nemmeno da un amministratore.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(revealedKey.key)
                setKeyCopied(true)
                setTimeout(() => setKeyCopied(false), 2000)
              }}
              className={cn(
                'flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                keyCopied
                  ? 'bg-success-50 text-success-700 border border-success-200'
                  : 'bg-primary-600 hover:bg-primary-700 text-white'
              )}
            >
              {keyCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {keyCopied ? 'Copiato!' : 'Copia'}
            </button>
            <button
              type="button"
              onClick={() => setRevealedKey(null)}
              className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 transition-colors"
            >
              Ho copiato la chiave
            </button>
          </div>
        </div>
      )}
    </Modal>
    </>
  )
}

// ============================================================
// Preferences Tab (with persistent theme)
// ============================================================

function PreferencesTab() {
  const theme = useUIStore((state) => state.theme)
  const setTheme = useUIStore((state) => state.setTheme)
  const [saved, setSaved] = useState(false)

  // The uiStore already uses persist middleware, so theme survives refresh.
  // We also apply the CSS class here for immediate effect.
  function handleThemeChange(t: 'light' | 'dark' | 'system') {
    setTheme(t)
    // Apply theme to document root for immediate visual effect
    const root = document.documentElement
    if (t === 'dark') {
      root.classList.add('dark')
    } else if (t === 'light') {
      root.classList.remove('dark')
    } else {
      // System: check prefers-color-scheme
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
      root.classList.toggle('dark', prefersDark)
    }
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  return (
    <div className="space-y-6">
      <SectionHeader title="Aspetto" description="Personalizza l'interfaccia" />

      <div>
        <label className="block text-xs font-medium text-slate-700 mb-2">Tema</label>
        <div className="inline-flex items-center gap-1 p-1 rounded-full bg-slate-100">
          {(['light', 'dark', 'system'] as const).map((t) => {
            const labels = { light: 'Chiaro', dark: 'Scuro', system: 'Sistema' }
            return (
              <button
                key={t}
                type="button"
                onClick={() => handleThemeChange(t)}
                className={cn(
                  'px-4 py-1.5 text-sm font-medium rounded-full transition-colors',
                  theme === t
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                )}
              >
                {labels[t]}
              </button>
            )
          })}
        </div>
        {saved && (
          <span className="flex items-center gap-1.5 text-xs text-success-600 font-medium mt-2">
            <Check className="w-3.5 h-3.5" />
            Tema salvato!
          </span>
        )}
        <p className="text-xs text-slate-400 mt-2">
          Le preferenze del tema vengono salvate automaticamente nel browser.
        </p>
      </div>

      <div className="border-t border-slate-200 pt-6">
        <SectionHeader title="Lingua" description="Lingua dell'interfaccia" />
        <select
          id="pref-language"
          aria-label="Lingua dell'interfaccia"
          disabled
          defaultValue="it"
          className={cn(inputClass, 'max-w-xs disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed')}
        >
          <option value="it">Italiano</option>
          <option value="en">English</option>
        </select>
        <p className="text-xs text-slate-400 mt-2">
          Il supporto multilingua completo sara' disponibile in una versione futura.
        </p>
      </div>

      <div className="border-t border-slate-200 pt-6">
        <SectionHeader title="Formato Data" description="Come vengono visualizzate le date" />
        <select
          id="pref-date-format"
          aria-label="Formato data"
          disabled
          defaultValue="dd/MM/yyyy"
          className={cn(inputClass, 'max-w-xs disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed')}
        >
          <option value="dd/MM/yyyy">GG/MM/AAAA (italiano)</option>
          <option value="MM/dd/yyyy">MM/GG/AAAA (americano)</option>
          <option value="yyyy-MM-dd">AAAA-MM-GG (ISO)</option>
        </select>
        <p className="text-xs text-slate-400 mt-2">
          La personalizzazione del formato data sara' disponibile a breve.
        </p>
      </div>
    </div>
  )
}

// ============================================================
// Notifications Tab (with localStorage persistence)
// ============================================================

function NotificationsTab() {
  const [prefs, setPrefs] = useState<NotificationPrefs>(loadNotifPrefs)
  const [savingKey, setSavingKey] = useState<string | null>(null)
  const [savedKey, setSavedKey] = useState<string | null>(null)

  function toggle(key: keyof NotificationPrefs) {
    const updated = { ...prefs, [key]: !prefs[key] }
    setPrefs(updated)
    setSavingKey(key)

    // Persist to localStorage immediately
    try {
      saveNotifPrefsLocal(updated)
    } catch {
      // Storage quota issue, ignore
    }

    // Attempt to sync to backend (fire-and-forget)
    apiClient
      .patch('/auth/profile', { preferences: { notifications: updated } })
      .then(() => {
        setSavedKey(key)
        setTimeout(() => setSavedKey(null), 1500)
      })
      .catch(() => {
        // Backend doesn't support preferences field yet — localStorage fallback is active
        setSavedKey(key)
        setTimeout(() => setSavedKey(null), 1500)
      })
      .finally(() => setSavingKey(null))
  }

  const notifications: { key: keyof NotificationPrefs; label: string; description: string }[] = [
    { key: 'taskAssigned', label: 'Task assegnato', description: 'Quando ti viene assegnato un task' },
    { key: 'taskDue', label: 'Scadenza task', description: 'Promemoria scadenze imminenti' },
    { key: 'commentMention', label: 'Menzione commenti', description: 'Quando vieni menzionato in un commento' },
    { key: 'projectUpdate', label: 'Aggiornamenti progetto', description: 'Modifiche ai tuoi progetti' },
    { key: 'clientUpdate', label: 'Aggiornamenti cliente', description: 'Modifiche ai profili cliente' },
    { key: 'emailDigest', label: 'Email riepilogo', description: 'Digest giornaliero via email' },
  ]

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Notifiche"
        description="Scegli quali notifiche ricevere. Le preferenze vengono salvate automaticamente."
      />

      <div className="space-y-3">
        {notifications.map(({ key, label, description }) => (
          <div
            key={key}
            className="flex items-center justify-between py-3 border-b border-slate-100 last:border-b-0"
          >
            <div>
              <p className="text-sm font-medium text-slate-800">{label}</p>
              <p className="text-xs text-slate-500 mt-0.5">{description}</p>
            </div>
            <div className="flex items-center gap-2 ml-4">
              {/* Mini status indicator */}
              {savingKey === key && (
                <Loader2 className="w-3 h-3 text-slate-400 animate-spin flex-shrink-0" />
              )}
              {savedKey === key && savingKey !== key && (
                <Check className="w-3 h-3 text-success-500 flex-shrink-0" />
              )}
              <button
                type="button"
                onClick={() => toggle(key)}
                disabled={savingKey === key}
                className={cn(
                  'relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 cursor-pointer disabled:opacity-60',
                  prefs[key] ? 'bg-primary-600' : 'bg-slate-200'
                )}
                role="switch"
                aria-checked={prefs[key]}
                aria-label={label}
              >
                <span
                  className={cn(
                    'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform',
                    prefs[key] ? 'translate-x-4' : 'translate-x-0'
                  )}
                />
              </button>
            </div>
          </div>
        ))}
      </div>

      <p className="text-xs text-slate-400">
        Le preferenze sono salvate localmente e sincronizzate con il server quando disponibile.
      </p>
    </div>
  )
}

// ============================================================
// SettingsPage
// ============================================================

export function SettingsPage() {
  const user = useAuthStore((state) => state.user)
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile')

  const isAdmin = (user?.role?.level ?? 99) <= 2

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Impostazioni</h1>
        <p className="text-sm text-slate-500 mt-1">
          Gestisci il tuo account e le preferenze
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
        {/* Sidebar tabs */}
        <div className="space-y-1">
          {TABS.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left',
                  activeTab === tab.id
                    ? 'bg-slate-100 text-slate-900'
                    : 'text-slate-600 hover:bg-slate-50'
                )}
              >
                <Icon className={cn('w-4 h-4 flex-shrink-0', activeTab === tab.id ? 'text-primary-600' : 'text-slate-400')} />
                {tab.label}
              </button>
            )
          })}

          {/* Admin-only section */}
          {isAdmin && (
            <>
              <div className="my-2 border-t border-slate-200" />
              <button
                onClick={() => navigate('/settings/users')}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left text-slate-600 hover:bg-slate-50"
              >
                <Shield className="w-4 h-4 flex-shrink-0 text-slate-400" />
                Gestione Utenti
              </button>
              <button
                onClick={() => navigate('/settings/audit')}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left text-slate-600 hover:bg-slate-50"
              >
                <History className="w-4 h-4 flex-shrink-0 text-slate-400" />
                Audit Trail
              </button>
            </>
          )}
        </div>

        {/* Tab content */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-card p-6">
          {activeTab === 'profile' && <ProfileTab />}
          {activeTab === 'security' && <SecurityTab />}
          {activeTab === 'preferences' && <PreferencesTab />}
          {activeTab === 'notifications' && <NotificationsTab />}
        </div>
      </div>
    </div>
  )
}
